//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { type ApiClient } from './apiClient';
import { type RawGraphSchema } from './apiTypes';

/**
 * LLM-backed operations: call an LLM, infer a graph schema from file data, and
 * translate a natural-language question to Cypher.  The active LLM (and, for
 * Cypher, the active dataset) are passed by the caller rather than read from a
 * slice.
 *
 * Methods are arrow fields so they keep a stable `this`-bound reference when
 * passed by value.
 */
export class LLMService {
  constructor(private readonly api: ApiClient) {}

  callLLM = (
    question: string,
    prompt_name: string,
    graph_schemas: RawGraphSchema[] | string[],
    llm: string,
    history: Array<{ role: string; content: string }>
  ): Promise<string> => {
    const data = {
      prompt_name: prompt_name,
      graph_schemas: graph_schemas,
      llm: llm,
      question: question,
      history: history,
    };

    return this.api.makeRequest<string>('/callLLM', 'post', data);
  };

  inferSchema = (
    filedata: unknown, llm: string, userPrompt: string
  ): Promise<string> => {
    const data = {
      filedata: filedata,
      llm: llm,
      userPrompt: userPrompt,
    };

    return this.api.makeRequest<string>('infer_schema', 'post', data);
  };

  getCypherFromQuestion = (
    question: string, activeDataset: string, llm: string
  ): Promise<string> => {
    const data = {
      dataset_name: activeDataset,
      llm: llm,
      question: question,
    };

    return this.api.makeRequest<string>(
      `/genAI?dataset_name=${encodeURIComponent(activeDataset)}`, 'post', data);
  };
}
